import { useState, useEffect } from "react"
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  TextField,
  TextareaAutosize,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@material-ui/core"
import Language from '../../services/language'
import { timezoneOptions } from '../../utils/userPreferenceSettings'
import { createTemplate, updateTemplate } from '../../services/whatsApp'

// ISO 639-1 Code
const supportedLangs = Language.get()

const supportedCategories = [
  {
    code: 'UTILITY',
    label: 'Utility'
  },
  {
    code: 'MARKETING',
    label: 'Marketing'
  }
]

const supportedActions = [
  {
    code: 'program_message',
    label: 'Program Message'
  },
  {
    code: 'invite',
    label: 'Invite message'
  },
  {
    code: 'subscribe',
    label: 'Subscribe message'
  },
  {
    code: 'unsubscribe',
    label: 'Unsubscribe message'
  },
  {
    code: 'notify_program_questionnaire',
    label: 'Program Questionnaire Notification'
  },
  {
    code: 'invalid_answer',
    label: 'Invalid Answer message'
  },
  {
    code: 'cancel',
    label: 'Cancel Questionnaire message'
  },
  {
    code: 'ask_timeofday',
    label: 'Ask Time of Day message'
  },
  {
    code: 'ask_timezone',
    label: 'Ask Timezone message'
  },
  {
    code: 'ask_frequency',
    label: 'Ask Frequency message'
  },
  {
    code: 'survey_welcome_message',
    label: 'Survey Welcome Message'
  },
  {
    code: 'survey_reminder_message',
    label: 'Survey Reminder Message'
  }
]

export const MakeWhatsAppTemplate = ({ organizationId, open, handleClose, template, countryCode }) => {
  const [label, setLabel] = useState('')
  const [language, setLanguage] = useState('en')
  const [message, setMessage] = useState('')
  const [action, setAction] = useState('program_message')
  const [published, setPublished] = useState(false)
  const [options, setOptions] = useState([])
  const [labelError, setLabelError] = useState(null)
  const [messageError, setMessageError] = useState(null)
  const [optionError, setOptionError] = useState(null)
  const [submitted, setSubmitted] = useState(false)
  const [category, setCategory] = useState('UTILITY')

  const getTimezoneOptionsByCC = (cc) => {
    return (timezoneOptions[cc] || []).map(tz => ({ key: tz.abbreviation, label: tz.abbreviation, value: tz.abbreviation }))
  }

  const optionsByActions = {
    ask_timeofday: {
      label: 'On Ask Time of Day',
      options: [
        {
          // value is the value the code sets, responseKey maps to this value for use in the code
          // code needs to be able to reference the key in some way to know what to do and what time to set
          // responseKey can be in any language and any text that the user must type for assigning "morning" for their interval
          // label is just used to show in the UI so user knows what they are entering the responseKey for
          responseKey: 'Morning',
          value: '0 9 * * *',
          label: 'Morning'
        },
        {
          responseKey: 'Afternoon',
          value: '0 12 * * *',
          label: 'Afternoon'
        },
        {
          responseKey: 'Evening',
          value: '0 18 * * *',
          label: 'Evening'
        }
      ]
    },
    ask_timezone: {
      label: 'On Ask Timezone',
      options: getTimezoneOptionsByCC(countryCode)
    }
  }

  useEffect(() => {
    if (template) {
      console.log('MakeWhatsAppTemplate.template', template)
      setLabel(template.label)
      setAction(template.action)
      setLanguage(template.language)
      setMessage(template.message)
      setPublished(template.published)
      setOptions(template.options || [])
    }
  }, [template])

  const validateMessage = (msg) => {
    // clear errors
    setMessageError(null)
    setLabelError(null)
    setOptionError(null)
    
    if (!msg) {
      setMessageError({message: 'Message is a required field, please enter a valid template message'})
      return false
    }

    switch (action) {
      case 'program_message': {
        // validate message
        const isValid = msg.length && msg.includes('{{body}}')

        if (!isValid) {
          setMessageError({message: 'Your message is missing required field, body. Please include {{body}} in your message'})
        }

        return isValid
      }
      default:
        return true
    }
  }

  const onActionChange = (e) => {
    setAction(e.target.value)
    setOptions([])

    if (optionsByActions[e.target.value]) {
      setOptions(optionsByActions[e.target.value].options)
    }
  }

  const onCategoryChange = (e) => {
    setCategory(e.target.value)
  }

  const handleSubmit = async () => {
    // handle update or create

    if (!label) {
      setLabelError({message: 'Label is a required field, please enter a valid label'})
      return
    }

    // validate message - based on action we can determine what should be allowed or not allowed in the message
    const isValidMessage = validateMessage(message)

    console.log('isValidMessage', isValidMessage)

    if (!isValidMessage) {
      return
    }

    if (options && options.length) {
      let optionHasError = false
      options.forEach(option => {
        console.log('handleSubmit.check.option', option)
        if (!option.responseKey) {
          // setMessageError({message: 'Response key is a required field for all options'})
          optionHasError = true
        }
      })

      if (optionHasError) {
        console.log('optionHasError')
        setOptionError({message: 'Keyword is a required field for all options'})
        return
      }
    }

    setSubmitted(true)

    if (template?.id) {
      // update
      const result = await updateTemplate({ organizationId, label, message, language, action, options, category, id: template.id })
      
      if (handleClose) {
        handleClose(result)
      }
    } else {
      try {
        const additionalParams = {}
        // this allows us to use buttons for survey welcome and reminder messages instead of printing the survey url in the message
        // this solves the issue of broken links when users click on the link in the message and it doesn't complete the url
        if (['survey_welcome_message', 'survey_reminder_message'].includes(action)) {
          additionalParams.type = 'call-to-action'
        }

        const result = await createTemplate({organizationId, label, message, language, action, options, category, ...additionalParams})

        if (handleClose) {
          handleClose(result)
        }
      } catch (e) {
        console.error('handleSubmit.error', e)
        setSubmitted(false)
      }

      console.log('handleSubmit', { organizationId, label, action, language, message, options, category })
    }
  }

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>{template ? 'Update' : 'Create'} WhatsApp Template</DialogTitle>
      <DialogContent>
        <FormControl error={!!labelError} fullWidth>
          <TextField
            autoFocus
            margin="dense"
            id="label"
            label="Label"
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            required
            disabled={published}
          />
          {labelError && <FormHelperText>{labelError.message}</FormHelperText>}
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Action</InputLabel>
          <Select
            name='action'
            value={action}
            onChange={onActionChange}
            required
            fullWidth
            disabled={published}
          >
            {
              supportedActions.map(act => <MenuItem key={act.code} value={act.code}>{act.label}</MenuItem>)
            }
          </Select>
          <FormHelperText>Select the action this template will be used for</FormHelperText>
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>Category</InputLabel>
          <Select
            name='category'
            value={category}
            onChange={onCategoryChange}
            required
            fullWidth
            disabled={published}
          >
            {
              supportedCategories.map(cat => <MenuItem key={cat.code} value={cat.code}>{cat.label}</MenuItem>)
            }
          </Select>
          <FormHelperText>Select the Category for this template</FormHelperText>
        </FormControl>
        
        <FormControl fullWidth>
          <InputLabel>Language</InputLabel>
          <Select
            name='language'
            value={language}
            onChange={e => setLanguage(e.target.value)}
            required
            fullWidth
            disabled={published}
          >
            {
              supportedLangs.map(lang => <MenuItem key={lang.code} value={lang.code}>{lang.label}</MenuItem>)
            }
          </Select>
          <FormHelperText>Select the language in which the template body will be written</FormHelperText>
        </FormControl>

        <FormControl error={!!messageError} fullWidth>
          <TextField
            name="message"
            label="Body"
            multiline
            maxRows={10}
            minRows={6}
            value={message}
            onChange={e => setMessage(e.target.value)}
            fullWidth
            required
            disabled={published}
          />
          {messageError && <FormHelperText>{messageError.message}</FormHelperText>}
        </FormControl>

        {
          options.length && (action !== 'survey_welcome_message' && action !== 'survey_reminder_message') ? (
            <Box>
              <Typography variant="h6" style={{marginTop: '1em'}}>Options</Typography>
              {
                options && options.length ? options.map((option, index) => (
                  <TextField
                    key={`${action}_option_${option.value}`}
                    margin="dense"
                    label={option.label}
                    type="text"
                    error={!!optionError}
                    value={option.responseKey}
                    onChange={e => {
                      let _options = [...options]
                      _options[index].responseKey = e.target.value
                      console.log('options.onChange', _options[index])
                      setOptions(_options)
                    }}
                    helperText={optionError ? optionError.message : "Enter the keyword you would like the user to enter for this option"}
                    fullWidth
                  />
                )) : null
              }
            </Box>
            ) : null
        }

        {
          action === 'survey_welcome_message' || action === 'survey_reminder_message' ? (
            <Box>
              <Typography variant="h6" style={{marginTop: '1em'}}>Call-To-Action</Typography>
              <Typography variant="body2">Action button will be displayed in the message to allow users to complete the survey.</Typography>
              <br />
              <Button variant="outlined">Complete Survey</Button>
            </Box>
          ) : null
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={() => handleClose(false)} color="primary" disabled={submitted}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" disabled={submitted}>
          {template ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}